import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiDelete } from "../shared/api"
import { escapeHtml, formatDate, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static values = {
    listingId: String,
    userSignedIn: { type: Boolean, default: false },
    currentUserId: { type: String, default: "" }
  }

  connect() {
    loadTranslations()
    this._load()
  }

  async _load() {
    try {
      const json = await apiGet(`/api/v1/marketplace/${this.listingIdValue}`)
      this.listing = json.data
      this._render()
    } catch (error) {
      console.error("Error loading listing:", error)
      this.element.innerHTML = `<div class="alert alert-danger">${escapeHtml(error.message)}</div>`
    }
  }

  _render() {
    const l = this.listing
    const hasGallery = l.media && l.media.length > 0
    const galleryCol = hasGallery ? this._renderGallery(l) : ""
    const contentColClass = hasGallery ? "col-md-7" : "col-12"
    const perms = l.permissions || {}

    this.element.innerHTML = `
      <div class="row">
        <div class="col-lg-8">
          <div class="card mb-4"><div class="row g-0">
            ${galleryCol}
            <div class="${contentColClass}">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <h3 class="card-title mb-0">${escapeHtml(l.title)}</h3>
                  <a class="forum-share-link text-muted" href="#" data-action="click->marketplace-show#openShareModal" title="${escapeHtml(t("share"))}">
                    <i class="fa-solid fa-share-nodes"></i>
                  </a>
                </div>
                <div class="marketplace-listing-attributes mb-2">
                  ${this._attrRow(t("category_label"), t(`categories.${l.category}`))}
                  ${this._attrRow(t("condition_label"), t(`conditions.${l.condition}`))}
                  ${l.color ? this._attrRow(t("color_label"), t(`colors.${l.color}`)) : ""}
                  ${l.size ? this._attrRow(t("size_label"), l.size) : ""}
                  ${l.embellishment === true ? this._attrRow(t("zdobiona_label"), t("embellishment_yes")) : ""}
                  ${l.embellishment === false ? this._attrRow(t("zdobiona_label"), t("embellishment_no")) : ""}
                </div>
                <hr class="my-3">
                <h6 class="mb-2">${escapeHtml(t("description_label"))}</h6>
                <p class="card-text">${this._simpleFormat(l.description || "-")}</p>
              </div>
            </div>
          </div></div>
        </div>
        <div class="col-lg-4">
          <div class="card"><div class="card-body">
            <h4 class="marketplace-price">${this._priceDisplay(l)}</h4>
            ${this._locationHtml(l)}
            ${this._datesHtml(l)}
            <hr>
            <h6>${escapeHtml(t("contact"))}</h6>
            ${this._contactHtml(l.contact_info)}
          </div></div>
          ${perms.can_report ? this._reportLinkHtml() : ""}
        </div>
      </div>
      <div class="mt-4 d-flex flex-wrap gap-2 align-items-center">
        <a href="/marketplace" class="default-btn"><i class="fa-solid fa-arrow-left me-2"></i>${escapeHtml(t("back_to_listings"))}</a>
        ${perms.can_edit ? `<a href="/marketplace/${l.id}/edit" class="default-btn"><i class="fa-solid fa-pen me-2"></i>${escapeHtml(t("common.edit", { default: "Edytuj" }))}</a>` : ""}
        ${perms.can_publish ? `<button class="default-btn cta-primary" data-action="click->marketplace-show#publish">${escapeHtml(t("publish"))}</button>` : ""}
        ${perms.can_renew ? `<button class="default-btn" data-action="click->marketplace-show#renew">${escapeHtml(t("renew"))}</button>` : ""}
        ${perms.can_close ? `<button class="default-btn" data-action="click->marketplace-show#close">${escapeHtml(t("close"))}</button>` : ""}
      </div>
      ${this._shareModalHtml(l)}
      ${perms.can_report ? this._reportModalHtml(l) : ""}`

    this._initShareModal()
    this._initLightcase()
  }

  _attrRow(label, value) {
    return `<div class="mb-1"><span class="fw-semibold me-1">${escapeHtml(label)}:</span>${escapeHtml(value)}</div>`
  }

  _renderGallery(l) {
    const gallery = l.media.filter(m => m.url || m.embed_url)
    if (gallery.length === 0) return ""
    const hero = gallery[0]
    const thumbs = gallery.slice(1)
    const galleryId = `marketplace-gallery-${l.id}`

    let heroHtml
    if (hero.media_type === "image" && hero.url) {
      heroHtml = `<a href="${escapeHtml(hero.url)}" data-rel="lightcase:${galleryId}" ${hero.image_width ? `data-lc-width="${hero.image_width}"` : ""} ${hero.image_height ? `data-lc-height="${hero.image_height}"` : ""} title="${escapeHtml(l.title)}"><img src="${escapeHtml(hero.medium_url || hero.url)}" alt="${escapeHtml(l.title)}" class="img-fluid rounded" style="object-fit: cover; width: 100%; height: 100%;"></a>`
    } else if (hero.media_type === "video") {
      const videoUrl = hero.url || hero.embed_url
      heroHtml = `<a href="${escapeHtml(videoUrl)}" data-rel="lightcase:${galleryId}" title="${escapeHtml(t("video_link"))}" class="marketplace-listing-gallery__hero-video bg-dark d-flex align-items-center justify-content-center"><i class="fa-solid fa-play-circle fa-4x text-white"></i></a>`
    } else {
      heroHtml = ""
    }

    let thumbsHtml = ""
    if (thumbs.length > 0) {
      thumbsHtml = `<div class="marketplace-listing-gallery__thumbs">${thumbs.map(m => {
        if (m.media_type === "image" && m.url) {
          return `<a href="${escapeHtml(m.url)}" data-rel="lightcase:${galleryId}" ${m.image_width ? `data-lc-width="${m.image_width}"` : ""} ${m.image_height ? `data-lc-height="${m.image_height}"` : ""} title="${escapeHtml(l.title)}" class="marketplace-listing-gallery__thumb"><img src="${escapeHtml(m.thumb_url || m.url)}" alt="${escapeHtml(l.title)}" class="img-fluid rounded w-100 h-100" style="object-fit: cover;"></a>`
        } else if (m.media_type === "video") {
          const vUrl = m.url || m.embed_url
          return `<a href="${escapeHtml(vUrl)}" data-rel="lightcase:${galleryId}" title="${escapeHtml(t("video_link"))}" class="marketplace-listing-gallery__thumb marketplace-listing-gallery__thumb--video rounded"><i class="fa-solid fa-play-circle fa-2x text-white"></i></a>`
        }
        return ""
      }).join("")}</div>`
    }

    return `<div class="col-md-5"><div class="marketplace-listing-gallery p-2"><div class="marketplace-listing-gallery__hero mb-2">${heroHtml}</div>${thumbsHtml}</div></div>`
  }

  _locationHtml(l) {
    const parts = []
    if (l.city) parts.push(l.city)
    if (l.voivodeship) {
      const label = t(`voivodeships.${l.voivodeship}`)
      parts.push(label !== `voivodeships.${l.voivodeship}` ? label : l.voivodeship)
    }
    if (parts.length === 0) return ""
    return `<p><i class="fa-solid fa-location-dot me-2"></i>${escapeHtml(parts.join(", "))}</p>`
  }

  _datesHtml(l) {
    if (!l.published_at && !l.expires_at && !l.updated_at) return ""
    let html = '<div class="mt-2 pt-2 border-top">'
    if (l.published_at) html += `<p class="mb-1 small text-muted">${escapeHtml(t("published_at"))}: ${formatDate(l.published_at)}</p>`
    if (l.expires_at) html += `<p class="mb-1 small text-muted">${escapeHtml(t("expires_at"))}: ${formatDate(l.expires_at)}</p>`
    if (l.updated_at && l.updated_at !== l.published_at) html += `<p class="mb-0 small text-muted">${escapeHtml(t("updated_at"))}: ${formatDate(l.updated_at)}</p>`
    html += "</div>"
    return html
  }

  _contactHtml(contact) {
    if (!contact) return `<p class="text-muted">${escapeHtml(t("no_contact"))}</p>`
    let html = ""
    if (contact.email) html += `<p class="mb-1"><a href="mailto:${escapeHtml(contact.email)}" class="text-decoration-none">${escapeHtml(contact.email)}</a></p>`
    if (contact.phone) html += `<p class="mb-0"><a href="tel:${escapeHtml(contact.phone)}" class="text-decoration-none">${escapeHtml(contact.phone)}</a></p>`
    if (!contact.email && !contact.phone) html = `<p class="text-muted">${escapeHtml(t("no_contact"))}</p>`
    return html
  }

  _priceDisplay(l) {
    if (!l.price_cents || l.price_cents === 0) return escapeHtml(t("free_giveaway"))
    return escapeHtml(l.price_formatted)
  }

  _simpleFormat(text) {
    return escapeHtml(text).replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>")
  }

  _reportLinkHtml() {
    return `<div class="card mt-4"><div class="card-body">
      <a class="forum-post-control-link text-muted" href="#" data-bs-toggle="modal" data-bs-target="#marketplaceReportModal">
        <i class="fa-solid fa-flag me-1"></i>${escapeHtml(t("report.link"))}
      </a>
    </div></div>`
  }

  _shareModalHtml(l) {
    return `<div class="modal fade" id="marketplaceShareModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">${escapeHtml(t("share_link"))}</h5>
          <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <input class="form-control" id="marketplaceShareUrlInput" type="text" readonly value="${escapeHtml(window.location.href)}">
            <button class="btn btn-outline-secondary" id="marketplaceCopyShareUrl" type="button"><i class="fa-solid fa-copy me-1"></i>${escapeHtml(t("copy_link"))}</button>
          </div>
          <div class="small text-success mt-2 d-none" id="marketplaceCopySuccess"><i class="fa-solid fa-check me-1"></i>${escapeHtml(t("link_copied"))}</div>
        </div>
      </div></div>
    </div>`
  }

  _reportModalHtml(l) {
    const reasons = ["spam", "inappropriate", "harassment", "misinformation", "other"]
    const options = reasons.map(r => `<option value="${r}">${escapeHtml(t(`report.reasons.${r}`))}</option>`).join("")

    return `<div class="modal fade" id="marketplaceReportModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">${escapeHtml(t("report.title"))}</h5>
          <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label" for="report_reason">${escapeHtml(t("report.reason"))}</label>
            <select class="form-select" id="report_reason"><option value=""></option>${options}</select>
          </div>
          <div class="mb-3">
            <label class="form-label" for="report_description">${escapeHtml(t("report.description"))}</label>
            <textarea class="form-control" id="report_description" rows="4" placeholder="${escapeHtml(t("report.description_placeholder"))}"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" type="button" data-bs-dismiss="modal">${escapeHtml(t("common.cancel", { default: "Anuluj" }))}</button>
          <button class="default-btn reverse" type="button" data-action="click->marketplace-show#submitReport">${escapeHtml(t("report.submit"))}</button>
        </div>
      </div></div>
    </div>`
  }

  openShareModal(e) {
    e.preventDefault()
    const modal = this.element.querySelector("#marketplaceShareModal")
    if (modal && window.bootstrap) new window.bootstrap.Modal(modal).show()
  }

  _initShareModal() {
    const copyBtn = this.element.querySelector("#marketplaceCopyShareUrl")
    const input = this.element.querySelector("#marketplaceShareUrlInput")
    const success = this.element.querySelector("#marketplaceCopySuccess")
    if (copyBtn && input && success) {
      copyBtn.addEventListener("click", () => {
        navigator.clipboard.writeText(input.value).then(() => {
          success.classList.remove("d-none")
          setTimeout(() => success.classList.add("d-none"), 3000)
        })
      })
    }
  }

  _initLightcase() {
    document.dispatchEvent(new CustomEvent('dancemate:lightcase:init'))
  }

  async publish() {
    try {
      await apiPost(`/api/v1/marketplace/${this.listingIdValue}/publish`)
      window.location.reload()
    } catch (e) { alert(e.message) }
  }

  async renew() {
    try {
      await apiPost(`/api/v1/marketplace/${this.listingIdValue}/renew`)
      window.location.reload()
    } catch (e) { alert(e.message) }
  }

  async close() {
    try {
      await apiPost(`/api/v1/marketplace/${this.listingIdValue}/close`)
      window.location.reload()
    } catch (e) { alert(e.message) }
  }

  async submitReport() {
    const reason = this.element.querySelector("#report_reason")?.value
    const description = this.element.querySelector("#report_description")?.value

    if (!reason) {
      alert(t("report.reason_required"))
      return
    }

    try {
      await apiPost(`/api/v1/marketplace/${this.listingIdValue}/report_abuse`, {
        marketplace_report: { reason, description }
      })
      const modal = this.element.querySelector("#marketplaceReportModal")
      if (modal && window.bootstrap) window.bootstrap.Modal.getInstance(modal)?.hide()
      alert(t("report.sent"))
    } catch (e) { alert(e.message) }
  }
}
